/**
 * Canonical error code taxonomy + wire envelope shared by every edge function and the platform
 * SPA (imported via the `@shared/` alias on both sides — see CLAUDE.md's `@shared/` alias table).
 * Codes follow the same flat `<namespace>.<attribute>` convention already used for Sentry custom
 * tags (`taxonomy.gap`, `document.url`); the nested object below is purely an ergonomics layer
 * over that same flat string — grouping by namespace at call sites, not a new encoding.
 */
export const ERROR_CODES = {
  VALIDATION: { INVALID_INPUT: "validation.invalid_input" },
  AUTH: { FORBIDDEN: "auth.forbidden", UNAUTHENTICATED: "auth.unauthenticated" },
  RATE_LIMIT: { EXCEEDED: "rate_limit.exceeded" },
  LLM: {
    PROVIDER_UNAVAILABLE: "llm.provider_unavailable",
    RATE_LIMITED: "llm.rate_limited",
    AUTH_ERROR: "llm.auth_error",
  },
  DOCUMENT: {
    FETCH_FAILED: "document.fetch_failed",
    SERVICE_UNAVAILABLE: "document.service_unavailable",
  },
  DATABASE: { UNAVAILABLE: "database.unavailable" },
  RESOURCE: { NOT_FOUND: "resource.not_found", CONFLICT: "resource.conflict" },
  CLAIM: { NOT_VERIFIED: "claim.not_verified" },
  INSIGHT: { NO_FIELDS: "insight.no_fields" },
  INTERNAL: { ERROR: "internal.error" },
} as const;

type LeafValues<T> = T extends string ? T : T extends object ? LeafValues<T[keyof T]> : never;

/** Machine-readable error identifier, "<namespace>.<attribute>" convention. */
export type ErrorCode = LeafValues<typeof ERROR_CODES>;

/**
 * Returns true for provider errors that are transient by nature (rate limit, temporary
 * unavailability) and already tracked via Statsig retry metrics — these should not generate
 * Sentry noise. Auth errors are intentionally excluded: they signal infra misconfiguration.
 */
export function isTransientProviderError(code: ErrorCode): boolean {
  return (
    code === ERROR_CODES.LLM.PROVIDER_UNAVAILABLE ||
    code === ERROR_CODES.LLM.RATE_LIMITED ||
    code === ERROR_CODES.DOCUMENT.SERVICE_UNAVAILABLE
  );
}

/** Classification of a provider error by retry semantics. */
export type ProviderErrorKind = "retryable" | "fatal";

/** LLM-specific extension: "client" means the caller sent a bad request — retrying won't help. */
export type LLMErrorKind = ProviderErrorKind | "client";

/** Canonical error body returned by every non-2xx JSON response. */
export interface ErrorBody {
  error: {
    code: ErrorCode;
    message: string;
    /** Free-form attributes characterizing the error, for client-side formatting.
     *  Never contains secrets; safe to log/display. */
    details?: Record<string, unknown>;
  };
}
